import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { checkQuota, limitsFor, type PlanId } from "./plans";
import { readPlanAndUsage, readPlanAndUsageAs } from "./plans.server";
import type { QuotaRow, QuotaSummary } from "./quotas";

/** Valfri nivå att förhandsvisa som – bara administratörer får effekt av den. */
const PreviewInput = z
  .object({ asPlan: z.enum(["free", "plus", "pro", "expert"]).nullish() })
  .partial();

export const getMyPlanStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => PreviewInput.parse(input ?? {}))
  .handler(async ({ data, context }) => {
    const status = await readPlanAndUsageAs(
      context.supabase as never,
      context.userId,
      (data?.asPlan ?? null) as PlanId | null,
    );
    const quota = checkQuota(status.plan, status.usage, status.unlimited);
    return { ...status, quota };
  });

/**
 * Administratörens testläge: som standard är ett adminkonto en helt vanlig
 * användare (så att köp och gränser går att testa på riktigt). Här kan man
 * välja att i stället köra obegränsat eller som en viss kontonivå.
 */
export const setAdminPlanMode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({ mode: z.enum(["self", "unlimited", "free", "plus", "pro", "expert"]) })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin-guard.server");
    await assertAdmin(context as never);
    const { error } = await (context.supabase as any)
      .from("user_preferences")
      .upsert({ user_id: context.userId, admin_plan_mode: data.mode }, { onConflict: "user_id" });
    if (error) throw new Error("Kunde inte spara testläget.");
    return { mode: data.mode };
  });




/**
 * Allt som är kvar den här perioden: annonser, djupskanningar, skanningar,
 * bildrensningar, omskrivningar och smarta sökningar.
 */
export const getMyQuotas = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<QuotaSummary> => {
    const status = await readPlanAndUsage(context.supabase as never, context.userId);
    const limits = limitsFor(status.plan, status.unlimited);
    const { PHOTO_SCAN_PER_DAY } = await import("./photo-scan");
    const { CLEAN_IMAGE_DAILY_LIMIT, CLEAN_IMAGE_MONTHLY_LIMIT } = await import("./clean-image");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const periodStart = status.periodStart ?? new Date(Date.now() - 30 * 864e5).toISOString();
    const dayStart = new Date(Date.now() - 864e5).toISOString();

    const count = async (
      table: string,
      since: string,
      extra?: (q: any) => any,
    ): Promise<number> => {
      let q = (supabaseAdmin as any)
        .from(table)
        .select("id", { count: "exact", head: true })

        .eq("user_id", context.userId)
        .gte("created_at", since);
      if (extra) q = extra(q);
      const { count: n } = await q;
      return n ?? 0;
    };

    const [
      deepUsed,
      videoUsed,
      photoUsed,
      cleanUsed,
      cleanUsedDay,
      rewriteUsed,
      searchUsed,
      searchUsedDay,
    ] = await Promise.all([
      count("scan_item_valuations", periodStart),
      count("scan_runs", dayStart, (q: any) => q.eq("kind", "video")),
      count("scan_runs", dayStart, (q: any) => q.eq("kind", "photos")),
      count("ai_calls", periodStart, (q: any) => q.eq("operation", "clean_image").eq("ok", true)),
      count("ai_calls", dayStart, (q: any) => q.eq("operation", "clean_image").eq("ok", true)),
      count("ad_rewrites", periodStart),
      count("ai_search_usage", periodStart),
      count("ai_search_usage", dayStart),
    ]);

    const videoPerDay = status.unlimited ? null : limits.video ? 10 : 0;
    const photoPerDay = status.unlimited ? null : PHOTO_SCAN_PER_DAY[status.plan];
    const cleanPerMonth = status.unlimited ? null : CLEAN_IMAGE_MONTHLY_LIMIT[status.plan];
    const cleanPerDay = status.unlimited ? null : CLEAN_IMAGE_DAILY_LIMIT[status.plan];

    const row = (
      id: QuotaRow["id"],
      label: string,
      window: QuotaRow["window"],
      used: number,
      limit: number | null,
      day?: { limit: number | null; used: number },
    ): QuotaRow => ({
      id,
      label,
      window,
      used,
      limit,
      remaining: limit === null ? null : Math.max(0, limit - used),
      included: limit === null || limit > 0,
      dayLimit: day ? day.limit : null,
      dayUsed: day?.used ?? 0,
      dayRemaining: day && day.limit !== null ? Math.max(0, day.limit - day.used) : null,
    });

    return {
      plan: status.plan,
      unlimited: status.unlimited,
      periodStart: status.periodStart,
      periodEnd: status.periodEnd,
      anchorDay: status.anchorDay,
      rows: [
        row("ads", "Annonser", "period", status.usage.month, limits.perMonth),
        row(
          "deepScans",
          "Djupskanningar",
          "period",
          deepUsed,
          status.unlimited ? null : limits.deepValuationsPerMonth,
        ),
        row("videoScans", "Videoskanningar", "day", videoUsed, videoPerDay),
        row("photoScans", "Fotoskanningar", "day", photoUsed, photoPerDay),
        row("cleanImages", "Bildrensningar", "period", cleanUsed, cleanPerMonth, {
          limit: cleanPerDay,
          used: cleanUsedDay,
        }),
        row(
          "rewrites",
          "Omskrivningar av text",
          "period",
          rewriteUsed,
          status.unlimited ? null : limits.aiRewritesPerMonth,
        ),
        row(
          "searches",
          "Smarta sökningar",
          "period",
          searchUsed,
          status.unlimited ? null : limits.aiSearchPerMonth,
          { limit: status.unlimited ? null : limits.aiSearchPerDay, used: searchUsedDay },
        ),
      ],
    };
  });
